
export type UserRole = 'ADMIN' | 'TECHNICIAN';

export interface User {
  id?: string;
  email: string;
  name: string;
  picture?: string;
  role: UserRole;
}

export interface Attachment {
  id: string;
  fileId?: string; // WorkDrive File ID
  fileName: string;
  fileType: string;
  size: number;
  url?: string; // WebLink (remote) or Object URL (transient)
  data?: string; // Base64 Data URI for local persistence and HTML embedding
  path?: string; // WorkDrive path
  bucket: 'summary' | 'issue_photos' | 'created_media' | 'received_media' | 'wechat' | 'old_backup' | 'new_backup' | 'parts' | 'other';
  uploaded: boolean;
  uploading: boolean;
  error?: string;
  issueId?: string; // If attached to a specific issue
  caption?: string; // For photos
  fieldRef?: string; // ID of the specific field (e.g., 'rootCause', 'fixItem-123')
}

export interface PartEntry {
  id: string;
  partNumber: string;
  description: string;
  quantity: string;
  notes: string;
  type: 'used' | 'needed' | 'waiting';
}

export interface Issue {
  id: string;
  title: string;
  category: string; // e.g., 'Electrical', 'Mechanical'
  resolved: boolean;
  urgency: 'Low' | 'Medium' | 'High' | 'Critical';
  
  // Details
  description: string; // Observation
  proposedFixes: { id: string; text: string; }[];
  troubleshootingSteps: { id: string; text: string; }[];
  
  // Resolution (if resolved)
  rootCause: string;
  fixApplied: string;
  verifiedBy: string;
  notes: string;
  solutionSummary: string; // Rich text summary

  // Content
  attachments: Attachment[];
  parts: PartEntry[];

  // Meta
  addToMfgReport: boolean;
  followUpRequired: boolean;
}

export interface Customer {
  id: string;
  companyName: string;
  contactPerson: string;
  position: string;
  address: string;
  phone: string;
}

export interface CuttingTest {
  id: string;
  parameterPhoto?: Attachment;
  parameterNotes: string;
  resultPhoto?: Attachment;
  resultNotes: string;
  isFinal: boolean;
}

export interface CuttingConditions {
  // Laser Config
  laserSource: string;
  laserPower: string;
  headSeries: string;
  headModel: string;
  machineType: 'SHEET' | 'TUBE';

  // Material
  materialType: string;
  materialGrade: string;
  thickness: string;
  assistGas: string;

  // Tube Specific
  profileType?: string;
  profileSize?: string;

  // Tests
  tests: CuttingTest[];
}

export interface ServiceReport {
  id: string; // UUID for internal tracking
  reportId?: string; // <YYYY>-<Counter> assigned on sync
  template?: ReportTemplate;
  status: 'DRAFT' | 'COMPLETED';
  createdAt: string;
  updatedAt: string;
  createdBy: string; // email
  technicianName: string;

  // Dates
  arrivalDate: string;
  departureDate: string;

  // Service Type
  serviceTypes: string[];

  // Customer
  customer: Customer;

  // Machine
  machine: {
    serialNumber: string;
    modelNumber: string;
    machineType: string;
    controllerType: string;
    softwareVersion: string;
  };

  // Content
  summary: string;
  attachments: Attachment[]; // Report-level attachments (summary photos, nameplates)
  issues: Issue[];

  // Parts
  parts: PartEntry[];

  // Other
  followUpRequired: boolean;
  designSuggestion: { current: string; problem: string; change: string; };
  internalSuggestion: string;
  toolsBought: { id: string; text: string; }[];
  toolsUsed: { id: string; text: string; }[];
  newNameplates: { id: string; text: string; }[];

  // New Sections
  cuttingConditions?: CuttingConditions;
  commissioning?: {
    checklist: { id: string; text: string; checked: boolean; }[];
    notes: string;
    signOff: string;
  };  installation?: {
    checklist: { id: string; text: string; checked: boolean; }[];
    notes: string;
    signOff: string;
  };
  training?: {
    trainees: string;
    topics: string;
    duration: string;
    notes: string;
  };

  // Sync State (not part of JSON export)
  _syncState?: {
    lastSaved: string;
    dirty: boolean;
    uploadQueue: string[]; // IDs of attachments pending upload
    version: number; // For conflict resolution
    isOffline: boolean;
  };
}

export interface AuditEvent {
  id: string;
  timestamp: string;
  actor: string;
  action: 'FIELD_CHANGE' | 'FILE_UPLOAD' | 'SAVE_DRAFT' | 'COMPLETE' | 'CREATE_EDITED_VERSION' | 'ERROR' | 'RETRY_UPLOAD';
  fieldPath?: string;
  oldValue?: string;
  newValue?: string;
  metadata?: any;
}

export const JOB_TYPES = [
  'Installation / Commissioning',
  'Preventive Maintenance',
  'Warranty Repair',
  'Billable Repair',
  'Training',
  'Retrofit',
  'Remote Support'
];

export const ISSUE_CATEGORIES = [
  'Not Specified',
  'Mechanical',
  'Electrical',
  'Software/Control',
  'Hydraulic',
  'Pneumatic',
  'Operator Error',
  'Process/Application'
];

export const LASER_SOURCES = ['Maxx', 'Raycus', 'IPG', 'nLIGHT'];

export const LASER_POWERS = ['3kW', '6kW', '12kW', '20kW', '30kW', '40kW', '50kW', '60kW'];

export const ASSIST_GASES = ['O2', 'N2', 'AIR'];

export const MATERIAL_GRADES: Record<string, string[]> = {
  'Carbon Steel': ['A36', '1018', '1045', 'A572-50', 'A516-70', 'AR400', 'AR500', 'Q235', 'Q345'],
  'Stainless Steel': ['304', '304L', '316', '316L', '430', '17-4 PH', '201'],
  'Aluminum': ['3003', '5052', '6061-T6', '7075', '1100'],
  'Brass': ['260 (Cartridge)', '360 (Free Machining)'],
  'Copper': ['110 (ETP)', '101 (Oxygen Free)'],
  'Titanium': ['Grade 1', 'Grade 2', 'Grade 5 (6Al-4V)'],
  'Galvanized': ['A653', 'Electro-Galv']
};

export const THICKNESS_METRIC = [
  '0.5', '0.8', '1.0', '1.2', '1.5', '2.0', '2.5', '3.0', '4.0', '5.0', 
  '6.0', '8.0', '10.0', '12.0', '14.0', '16.0', '18.0', '20.0', '22.0', 
  '25.0', '30.0', '35.0', '40.0', '45.0', '50.0', '60.0', '70.0', '76.0'
];

export const THICKNESS_IMPERIAL = [
  '28ga', '26ga', '24ga', '22ga', '20ga', '18ga', '16ga', '14ga', '12ga', 
  '11ga', '10ga', '8ga', '7ga', '3ga', 
  '1/4"', '5/16"', '3/8"', '1/2"', '5/8"', '3/4"', '7/8"', '1"', 
  '1-1/4"', '1-1/2"', '1-3/4"', '2"', '2-1/2"', '3"'
];

export type ReportTemplate = 'SERVICE' | 'INSTALLATION' | 'COMMISSIONING' | 'TRAINING' | 'CUTTING';